/**
 * antd 1.x message 包装器: 给所有提示 (success/info/warning/error) 加「关闭按钮」+ 按文本「去重」.
 *
 * 背景: antd message 默认没有关闭按钮, 只能靠 duration 自动消失; 读卡器这类
 * 连接失败提示停留 6 秒, 用户点不掉. 同时 EntryV1 是 keep-alive 页, 每次重新
 * 进入都会再调一次设备预检, 失败就再弹一条, 视觉上像"关不掉".
 *
 * 实现 (利用 antd message 原生能力, 见 node_modules/.../es/message/index.js):
 *   - content 传 VNode: 在原文本后渲染一个 × span, 点击调用 message 返回的
 *     关闭函数 (内部 removeNotice) 立即关掉这一条.
 *   - key 复用: 同一文本在 duration 窗口内用相同 key, antd 会复用同一条 notice
 *     而不是再弹一条, 实现去重.
 *
 * 用法 (main.js):
 *   import message from 'ant-design-vue/es/message'
 *   import { patchMessage } from '@/utils/messageClose'
 *   patchMessage(message)            // 包装 message 本体 (供直接 import 的代码)
 *   patchMessage(Vue.prototype.$message) // 包装 $message (供 this.$message 的代码)
 */
import Vue from 'vue'

// 文本 → 稳定 key 的哈希 (djb2), 用于去重. 不追求密码学强度, 只求同文本同 key.
function textKey(text) {
  const s = String(text == null ? '' : text)
  let hash = 5381
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0
  }
  return 'msg_' + (hash >>> 0).toString(36)
}

/**
 * 把原始文本 + 关闭按钮渲染成 VNode 数组 (供 antd message 的 content 用).
 * @param {Function} h           Vue 的 createElement
 * @param {String|VNode} content 原始内容 (字符串或已有 VNode)
 * @param {Function} close       关闭回调 (message 返回的关闭函数)
 */
function renderWithClose(h, content, close) {
  const textNode = typeof content === 'string'
    ? h('span', { class: 'msg-text' }, content)
    : content
  const closeBtn = h('span', {
    class: 'msg-close',
    attrs: { role: 'button', 'aria-label': '关闭', title: '关闭' },
    on: { click: close }
  }, '×')
  return h('span', { class: 'msg-with-close' }, [textNode, closeBtn])
}

/**
 * 包装一个 message 对象 (antd message 实例 或 Vue.prototype.$message).
 * 仅改写 success/info/warning/error 四个方法; loading/destroy/config 等保持原样.
 */
export function patchMessage(msg) {
  if (!msg || msg.__closePatched) return msg
  msg.__closePatched = true

  // success/info/warning/error 统一加 × 关闭按钮 + 按文本去重.
  // (loading 不在此列, 它是加载态, 通常由业务用 destroy() 或手动移除.)
  ;['success', 'info', 'warning', 'error'].forEach((type) => {
    const original = msg[type]
    if (typeof original !== 'function') return
    msg[type] = function closable(content, duration, onClose) {
      // 调用方可能传对象 { content, duration, key } (本项目读卡器调用点就是这样传),
      // 也可能传 (content, duration, onClose). 统一归一化成对象再决定怎么走.
      let opts
      if (content && typeof content === 'object' && content.content !== undefined) {
        opts = { content: content.content, duration, onClose, key: content.key }
      } else {
        if (typeof duration === 'function') {
          onClose = duration
          duration = undefined
        }
        opts = { content, duration, onClose }
      }

      // 所有类型: 加关闭按钮 + 按文本去重 (duration 窗口内同文本只显示一条)
      const dedupKey = opts.key || textKey(opts.content)
      // 先调一次原始方法拿到关闭函数; content 这里先用占位, 因为关闭函数要等返回.
      // 但 antd message 的 content 可以是函数 (h) => VNode, 在渲染时才需要 close,
      // 而关闭函数就是返回值本身 —— 所以用一个变量在渲染时引用它即可.
      let closer = null
      const closeFn = () => { if (closer) closer() }
      const vnodeContent = (h) => renderWithClose(h, opts.content, closeFn)
      closer = original.call(msg, { content: vnodeContent, duration: opts.duration, key: dedupKey })
      // antd message 返回的关闭函数还带 .then/.promise, 这里只暴露关闭能力即可
      if (typeof opts.onClose === 'function') {
        // onClose 在自动/手动关闭时都应触发: 用返回值的 promise 兜底
        try { closer && closer.then && closer.then(opts.onClose) } catch (e) {}
      }
      return closer
    }
  })

  return msg
}

export default patchMessage
